import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Box, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DateTime, FixedOffsetZone } from "luxon";
import AppLayout from "works/chi/components/AppLayout";
import StyledButton from "works/chi/components/StyledButton";
import Loading from "default/components/Loading";
import { getUserTimezone, putUserTimezone, getTimezoneOptions } from "utils/api";

export function previewTime(zone, locale) {
  if (!zone) return "";
  const match = /^UTC([+-])(\d{2}):(\d{2})$/.exec(zone);
  const resolved = match
    ? FixedOffsetZone.instance((Number(match[2]) * 60 + Number(match[3])) * (match[1] === "+" ? 1 : -1))
    : zone;
  return DateTime.now().setZone(resolved).setLocale(locale).toLocaleString(DateTime.DATETIME_FULL);
}

const buttonSx = {
  color: "#FEFEFE", boxShadow: "none", width: "100%", maxWidth: 400,
  height: "auto", minHeight: 54, py: 1.5, px: 2, lineHeight: 1.6,
  "&:hover": { backgroundColor: "rgba(255,255,255,0.08)", boxShadow: "none" },
  "&.Mui-disabled": { color: "#AAAAAA", borderColor: "#777777" },
};
const fieldSx = {
  "& .MuiInputBase-root": { color: "#FEFEFE", backgroundColor: "rgba(0,0,0,0.3)", fontFamily: "Noto Serif JP, serif" },
  "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": { color: "#FEFEFE" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.55)" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FEFEFE" },
  "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": { color: "#FEFEFE" },
};
const paperSx = {
  bgcolor: "#242424", color: "#FEFEFE", fontFamily: "Noto Serif JP, serif",
  border: "1px solid #777777",
  "& .MuiAutocomplete-option.Mui-focused": { bgcolor: "#444444" },
  "& .MuiAutocomplete-option[aria-selected='true']": { bgcolor: "#555555" },
  "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": { bgcolor: "#666666" },
  "& .MuiAutocomplete-noOptions": { color: "#FEFEFE" },
};

export default function TimezoneSettings() {
  const { t, i18n } = useTranslation("chi");
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState(null);
  const [options, setOptions] = useState([]);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [, tick] = useState(0);
  const [deviceZone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const text = (key) => t(`timezoneSettings.${key}`);

  useEffect(() => {
    let active = true;
    Promise.all([getUserTimezone(), getTimezoneOptions()]).then(([current, choices]) => {
      if (!active) return;
      setSettings(current);
      setSelected(current.timezone);
      setOptions([...choices.regions, ...choices.fixed_offsets]);
    }).catch(() => active && setError(true));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => tick((value) => value + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!settings?.pending || busy) return undefined;
    let active = true;
    const timer = setInterval(async () => {
      try {
        const current = await getUserTimezone();
        if (active) { setSettings(current); setError((previous) => previous === "save" ? previous : false); }
      } catch (_) {
        if (active) setError(true);
      }
    }, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [settings?.pending, busy]);

  async function save(value) {
    setBusy(true); setError(false); setSaved(false);
    try {
      const current = await putUserTimezone(value);
      setSettings(current); setSelected(current.timezone); setSaved(true);
    } catch (_) { setError("save"); }
    finally { setBusy(false); }
  }

  return <AppLayout>
    <Box sx={{ py: 4, maxWidth: 560, mx: "auto", color: "white", fontFamily: "Noto Serif JP, serif", "& .MuiTypography-root": { fontFamily: "inherit", lineHeight: 1.8 }, display: "flex", flexDirection: "column", gap: 3, alignItems: "center" }}>
      {error && <Alert severity="error" role="alert">{text(error === "save" ? "saveError" : "error")}</Alert>}
      {!settings && !error && <Loading />}
      {settings && <>
        <Typography>{text("current")}: {settings.timezone || text("default")}</Typography>
        <Autocomplete fullWidth options={options} value={selected} disabled={busy}
          onChange={(_, value) => { setSelected(value); setSaved(false); }}
          slotProps={{ paper: { sx: paperSx } }}
          renderInput={(params) => <TextField {...params} label={text("select")} InputLabelProps={{ shrink: true }} placeholder="Tokyo / UTC+03:00" />}
          sx={fieldSx} />
        <Typography aria-live="polite">{selected ? previewTime(selected, i18n.language) : text("default")}</Typography>
        <StyledButton variant="contained" sx={buttonSx} disabled={busy || !options.includes(deviceZone)} onClick={() => { setSelected(deviceZone); setSaved(false); }}>
          {text("device")}: {deviceZone}
        </StyledButton>
        <StyledButton variant="contained" sx={buttonSx} disabled={busy || !selected || !options.includes(selected)} onClick={() => save(selected)}>{text("save")}</StyledButton>
        <StyledButton variant="contained" sx={buttonSx} disabled={busy} onClick={() => save(null)}>{text("reset")}</StyledButton>
        <Box role="status">
          {settings.pending ? text("pending") : saved ? text("saved") : ""}
        </Box>
      </>}
    </Box>
  </AppLayout>;
}
